import type { ReactNode } from 'react';
import { Callout, ChangeOutcome, TechnicalDetails } from '@beyond-js/ui/react';
import type { Refusal } from '@snapshots/transport';
import { Problems, type Attempt } from './problems';

type Text = (key: string, values?: Record<string, string | number>) => string;

/**
 * Something that did not happen, said in place (decision D43 as amended on 2026-10-03): the sentence
 * says what happened, whether anything changed and the way on, and "Technical details" holds the service's
 * own words, the request it names and the time, copyable for support. Everything here is said in the warning
 * tone, in a polite live region. A change that met an outage is said the family's way (D186, `ChangeOutcome`
 * of `@beyond-js/ui`): answered as unavailable, who did not answer, "Nothing was changed." and the shared
 * Try again when `again` is given, the person's own; with no answer, "The change may have been made." and
 * `check`, the way to check before trying again. A read nobody answered is not said here: it is the page's one line (`Outages`, D163),
 * and its place says it is not available right now (`Unanswered`).
 */
export function Failure({ refusal, text, locale, actions, attempt = 'change', size, enable, check, again }: { refusal: Refusal; text: Text; locale: string; actions?: ReactNode; attempt?: Attempt; size?: (bytes: number) => string; enable?: boolean; check?: { label: string; onSelect: () => void }; again?: () => unknown }) {
	const language = locale === 'es' ? 'es' : 'en';
	const outcome = attempt === 'change' ? Problems.outcome(text, refusal, locale) : null;
	const details = { ...Problems.details(refusal), locale, labels: TechnicalDetails.labels[language] };
	return (
		<div data-failure={refusal.code} data-request={refusal.request} data-outcome={outcome?.outcome}>
			{outcome ? (
				<ChangeOutcome outcome={outcome.outcome} cause={outcome.cause} check={outcome.outcome === 'unknown' ? check : null} onAgain={again} details={outcome.details} labels={ChangeOutcome.labels[language]} />
			) : (
				// The shared callout keeps its evidence, reading measure and actions in one group.
				<Callout tone="warning" live title={Problems.say(text, refusal, attempt, { size, enable })} actions={actions} details={details} />
			)}
		</div>
	);
}
